import { Checkbox, CheckboxReadonly } from "components/Atoms/inputs/Checkbox";
import React from "react";
import { ExternalPermProp, InternalPermProp, Section } from "./permissionsectioncreator";
import { useCurrentUser } from "common/SessionContext";

interface PermissionGroupProps {
  readonly?: boolean
  userToClientId: number
  userId: number
  permissions: server.dto.InternalEnvironmentPermissions | server.dto.ExternalEnvironmentPermissions
  permissionGroup: Section<server.dto.InternalEnvironmentPermissions | server.dto.ExternalEnvironmentPermissions>
  onChange: (updatedPermissions: server.dto.InternalEnvironmentPermissions | server.dto.ExternalEnvironmentPermissions) => void
}

export const PermissionGroup = (props: PermissionGroupProps) => {
  const currentUser = useCurrentUser();
  const onToggle = (toggledProp: InternalPermProp | ExternalPermProp) => {
    const newValue = !props.permissions[toggledProp];

    if (toggledProp === "configureSystemSettings" && !newValue && props.userId === currentUser.userId) {
      window.displayGenericModal(RESX.GeneralWarnings.Warning, RESX.SoUser.YouCantMakeThisChangeToYourOwnAccount, "tiny");
      return;
    }

    const newPermissions = {
      ...props.permissions,
      [toggledProp]: newValue
    };

    let itemsToDisable = disableMap[toggledProp];

    // If costs AND sales are both disabled, totals should be disabled too
    if ((toggledProp === "readCosts" && !props.permissions.readSales)
      || (toggledProp === "readSales" && !props.permissions.readCosts)
    ) {
      itemsToDisable = ["readTotals"];
    }

    if (!newValue && itemsToDisable) {
      itemsToDisable.forEach(prop => {
        newPermissions[prop] = false;
      });
    }

    const itemsToEnable = enableMap[toggledProp];
    if (newValue && itemsToEnable) {
      itemsToEnable.forEach(prop => {
        newPermissions[prop] = true;
      })
    }

    props.onChange(newPermissions);
  };

  return <div>
    <h4 className="margin-bottom-small">{props.permissionGroup.label}</h4>
    <div className={props.readonly ? "" : "flex-column gap-mini"}>
      {props.permissionGroup.permissions.map(perm =>
        props.readonly ?
          <CheckboxReadonly
            key={perm.propName}
            checked={props.permissions[perm.propName]}
            label={perm.label}
          /> :
          <Checkbox
            label={perm.label}
            key={perm.propName}
            checked={props.permissions[perm.propName]}
            disabled={perm.disabled}
            onChange={() => {
              onToggle(perm.propName);
            }}
          />
      )}
    </div>
  </div>
}

// When the key gets disabled, all its values get disabled too
// For example: if {"access": ["modify", "delete"]} means if "access" gets disabled, "modify" and "delete" get disabled too
const disableMap: Partial<{ [propName in InternalPermProp | ExternalPermProp]: InternalPermProp[] }> = {
  modifyInAddressBook: ["deleteInAddressBook"],

  modifyInPlanning: ["deleteInPlanning"],

  accessQuotes: ["modifyQuotes", "deleteQuotes", "exportQuotes"],
  modifyQuotes: ["deleteQuotes"],

  accessPurchaseAgreements: ["modifyPurchaseAgreements", "deletePurchaseAgreements", "exportPurchaseAgreements"],
  modifyPurchaseAgreements: ["deletePurchaseAgreements"],

  accessContracts: ["modifyContracts", "deleteContracts", "exportContracts"],
  modifyContracts: ["deleteContracts"],

  accessInvoices: ["modifyInvoices", "deleteInvoices", "exportInvoices", "modifyPayments"],
  modifyInvoices: ["deleteInvoices"],
};

// When the key gets enabled, all it's values get enabled too
const enableMap: Partial<{ [propName in InternalPermProp | ExternalPermProp]: InternalPermProp[] }> = {
  configureSystemSettings: ["configureTemplates", "configureBookingForms", "configureEmbedShows"]
};